import { CommonModule } from '@angular/common';
import { Component, EventEmitter, NgModule, OnInit, Output } from '@angular/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { MenuText, Restaurant, RestaurantMenu, WeeklyRestaurantMenu } from '@app/models/restaurant-menu';
import { LinkModule } from '@app/directives/link.directive';
import { RestaurantMenuService } from '@app/services/restaurant-menu.service';
import { RestaurantService } from '@app/services/restaurant.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { endOfISOWeek, getISOWeek, startOfISOWeek } from 'date-fns';
import { BehaviorSubject, EMPTY, forkJoin, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-compact-restaurant-menu',
  templateUrl: './compact-restaurant-menu.component.html',
  styleUrls: ['./compact-restaurant-menu.component.scss']
})
export class CompactRestaurantMenuComponent implements OnInit {

  private changeSubject = new BehaviorSubject<number>(0);
  selectedRestaurant: number;
  restaurants$: Observable<Restaurant[]>;
  restaurantMenus$: Observable<RestaurantMenu[]>;
  currentWeekNumber: number;
  startOfISOWeek: Date;
  endOfISOWeek: Date;
  weeks: WeeklyRestaurantMenu[];
  menuCardsToDisplay: WeeklyRestaurantMenu[];
  numberOfMenuCardPerLoad = 5;
  currentMenusToShow: WeeklyRestaurantMenu[];
  startIndex = 0;
  numberOfShowMoreClicks = 0;
  maxNumberOfShowMoreClicks: number;
  @Output() hideRestaurantMenu = new EventEmitter<any>();

  constructor(private restaurantService: RestaurantService,
              private userChannelTermsService: UserChannelTermsService,
              private lunchMenuService: RestaurantMenuService) { }

  handleSelection(event: MatSelectChange) {
    this.changeSubject.next(event.value);
  }

  ngOnInit() {
    // we treat week number according to ISO-8601 https://date-fns.org/docs/getISOWeek
    this.currentWeekNumber = getISOWeek(new Date());
    this.startOfISOWeek = startOfISOWeek(new Date());
    this.endOfISOWeek = endOfISOWeek(new Date());

    this.restaurants$ = forkJoin(
      [
        this.restaurantService.getAllRestaurants(),
        this.userChannelTermsService.getUserChannelTerms()
      ])
      .pipe(
        map(([restaurants, userChannels]) => {
          return restaurants.map(restaurant => {
            restaurant.UserChannelMatched = restaurant.ChannelTermRestaurants.some(restaurantChannel => userChannels.some(userChannel => userChannel.ChannelTermId === restaurantChannel.ChannelTermId));
            return restaurant;
          });
        }),
        tap(restaurants => {
          if (restaurants && restaurants.length > 0) {
            const matchedRestaurant = restaurants.find(restaurant => restaurant.UserChannelMatched) ?? restaurants.find(restaurant => restaurant.ChannelTermRestaurants.length === 0) ?? restaurants[0];
            this.selectedRestaurant = matchedRestaurant.Id;
            this.changeSubject.next(this.selectedRestaurant);
            this.hideRestaurantMenu.emit(false);
          } else {
            this.hideRestaurantMenu.emit(true);
          }
        }));

    this.restaurantMenus$ = this.changeSubject
      .pipe(
        mergeMap(id => {
          if (id > 0) {
            return this.lunchMenuService.getCurrentAndFutureRestaurantMenusForRestaurant(id)
              .pipe(
                map(menus => {
                  // get today's date day
                  this.weeks = new Array<WeeklyRestaurantMenu>();
                  const todayDate = new Date();
                  const today = todayDate.toLocaleDateString('en-US', { weekday: 'long' });
                  const currentWeekNumber = getISOWeek(new Date());
                  menus.map(weekMenu => {
                    const week = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                    // if week number is current week then skip the days passed to update.
                    if (weekMenu.WeekNumber === currentWeekNumber) {
                      // get the index of the week day from the array of week
                      const weekIndex = week.findIndex(d => d === today);
                      const daysInCurrentWeek = week.slice(weekIndex, week.length);
                      this.parseWeekDays(daysInCurrentWeek, weekMenu);
                    } else {
                      this.parseWeekDays(week, weekMenu);
                    }
                  });
                  this.maxNumberOfShowMoreClicks = Math.floor((this.weeks.length - 1) / this.numberOfMenuCardPerLoad);
                  this.menuCardsToDisplay = this.weeks.slice(0, this.numberOfMenuCardPerLoad);
                  return menus;
                }));
          } else {
            return EMPTY;
          }
        }));
  }

  parseWeekDays(weekDays: string[], weekMenu: RestaurantMenu) {
    weekDays.forEach(d => {
      if (weekMenu[d] !== null && weekMenu[d] !== '') {
        // check if a week number and day already exists in the weeks array
        if (!this.weeks.some(w => w.WeekNumber === weekMenu.WeekNumber && w.WeekDay === d)) {
          this.weeks.push(this.parseWeeklyMenus(weekMenu.WeekNumber, d, weekMenu[d]));
        } else {
          const week = this.weeks.find(w => w.WeekNumber === weekMenu.WeekNumber && w.WeekDay === d);
          week.menu.push(new MenuText({
            value: weekMenu[d]
          }));
        }
      }
    });
    if (weekMenu['AllWeek'] !== null && weekMenu['AllWeek'] !== '') {
      if (!this.weeks.some(w => w.WeekNumber === weekMenu.WeekNumber && w.WeekDay === 'w' + weekMenu.WeekNumber)) {
        this.weeks.push(this.parseWeeklyMenus(weekMenu.WeekNumber, 'w' + weekMenu.WeekNumber, weekMenu.AllWeek));
      } else {
        const week = this.weeks.find(w => w.WeekNumber === weekMenu.WeekNumber && w.WeekDay === 'w' + weekMenu.WeekNumber);
        week.menu.push(new MenuText({
          value: weekMenu.AllWeek
        }));
      }
    }
    this.weeks.sort((a, b) => {
      return a.WeekNumber - b.WeekNumber;
    });
  }

  parseWeeklyMenus(WeekNumber: number, day: string, dayMenu: string): WeeklyRestaurantMenu {
   return new WeeklyRestaurantMenu({
      WeekNumber: WeekNumber,
      WeekDay: day,
      menu: [{value: dayMenu}] as MenuText[]
    });
  }

  clickShowPrevious() {
    this.menuCardsToDisplay = this.weeks.slice(this.startIndex - this.numberOfMenuCardPerLoad , this.startIndex);
    this.numberOfShowMoreClicks -= 1; // 1
    this.startIndex -= this.numberOfMenuCardPerLoad;
  }

  clickShowNext() {
    this.numberOfShowMoreClicks++;
    this.startIndex =  this.numberOfMenuCardPerLoad * this.numberOfShowMoreClicks;
    this.menuCardsToDisplay = this.weeks.slice(this.startIndex, this.startIndex + this.numberOfMenuCardPerLoad);
  }
}

@NgModule({
  declarations: [CompactRestaurantMenuComponent],
  exports: [CompactRestaurantMenuComponent],
  imports: [
    CommonModule,
    MatSelectModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    LinkModule,
    MatButtonModule
  ],
})
export class CompactRestaurantMenuModule { }
